import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import { generateId } from '../../shared/utils/idGenerator';
import i18n from '../../i18n';

const tr = (key, opts) => i18n.t(`serviceMessages.transporters.${key}`, opts);

const TransportersContext = createContext();

export const useTransporters = () => {
  const context = useContext(TransportersContext);
  if (!context) {
    throw new Error(tr('hook_outside_provider'));
  }
  return context;
};

export const TransportersProvider = ({ children }) => {
  const [transporters, setTransporters] = useState([]);

  // Initialize with some default transporters if none exist
  useEffect(() => {
    const initializeTransporters = () => {
      const savedTransporters = localStorage.getItem('transporters');
      if (savedTransporters) {
        try {
          setTransporters(JSON.parse(savedTransporters));
        } catch (error) {
          console.error('Error parsing saved transporters:', error);
          setDefaultTransporters();
        }
      } else {
        setDefaultTransporters();
      }
    };

    const setDefaultTransporters = () => {
      // Start with empty transporters list - user will create their own
      const defaultTransporters = [];
      setTransporters(defaultTransporters);
      // Don't save empty array to localStorage - let it stay undefined
    };

    initializeTransporters();
  }, []);

  // Save to localStorage whenever transporters change
  useEffect(() => {
    if (transporters.length > 0) {
      localStorage.setItem('transporters', JSON.stringify(transporters));
    }
  }, [transporters]);

  const addTransporter = useCallback(async (transporterData) => {
    try {
      const newTransporter = {
        id: generateId(),
        name: transporterData.name || '',
        contact: transporterData.contact || '',
        phone: transporterData.phone || '',
        email: transporterData.email || '',
        address: transporterData.address || '',
        vehicleCount: parseInt(transporterData.vehicleCount) || 0,
        rating: parseFloat(transporterData.rating) || 0,
        status: transporterData.status || 'active',
        specialties: transporterData.specialties || [],
        createdAt: new Date().toISOString()
      };

      setTransporters(prev => [...prev, newTransporter]);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'transporters');
      
      return newTransporter;
    } catch (error) {
      console.error('Error adding transporter:', error);
      throw error;
    }
  }, []);

  const updateTransporter = useCallback(async (id, updatedData) => {
    try {
      const updatedTransporters = transporters.map(transporter =>
        transporter.id === id
          ? { 
              ...transporter, 
              ...updatedData,
              vehicleCount: parseInt(updatedData.vehicleCount) || transporter.vehicleCount,
              rating: parseFloat(updatedData.rating) || transporter.rating,
              updatedAt: new Date().toISOString()
            }
          : transporter
      );

      setTransporters(updatedTransporters);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'transporters');
      
      return updatedTransporters.find(t => t.id === id);
    } catch (error) {
      console.error('Error updating transporter:', error);
      throw error;
    }
  }, [transporters]);

  const deleteTransporter = useCallback(async (id) => {
    try {
      const transporterToDelete = transporters.find(t => t.id === id);
      if (!transporterToDelete) {
        throw new Error(tr('not_found', { id }));
      }

      const updatedTransporters = transporters.filter(transporter => transporter.id !== id);
      setTransporters(updatedTransporters);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'transporters');
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting transporter:', error);
      throw error;
    }
  }, [transporters]);

  const getTransporterById = useCallback((id) => {
    return transporters.find(transporter => transporter.id === id);
  }, [transporters]);

  const getActiveTransporters = useCallback(() => {
    return transporters.filter(transporter => transporter.status === 'active');
  }, [transporters]);

  const refreshData = useCallback(() => {
    // For now, just re-read from localStorage
    const savedTransporters = localStorage.getItem('transporters');
    if (savedTransporters) {
      try {
        setTransporters(JSON.parse(savedTransporters));
      } catch (error) {
        console.error('Error refreshing transporters:', error);
      }
    }
  }, []);

  return (
    <TransportersContext.Provider value={{
      transporters,
      addTransporter,
      updateTransporter,
      deleteTransporter,
      getTransporterById,
      getActiveTransporters,
      refreshData
    }}>
      {children}
    </TransportersContext.Provider>
  );
};


